import React from 'react'
import { Text } from 'components/ds'
import styles from './index.module.css'

type Props = {
  caption: React.ReactNode
  media?: React.ReactNode
  title: React.ReactNode
  large?: boolean
}

/**
 * A media item component that displays content with an optional media thumbnail.
 * Commonly used for displaying media files, tracks, or other content with visual representation.
 *
 * @param caption - The secondary text or description to display below the title
 * @param media - Optional media element (image, icon, etc.) to display alongside the content
 * @param title - The primary title or name of the media item
 * @param large - Opt into the larger, bolder typography used by prominent surfaces such as modal headers
 * @returns A media item layout with optional media thumbnail and title/caption text
 */
const MediaItem = ({
  caption,
  media,
  title,
  large,
}: Props): React.ReactElement<'div'> => {
  return (
    <div className={styles.mediaItem}>
      {media && <div className={styles.mediaItemMedia}>{media}</div>}
      <div className={styles.mediaItemContent}>
        <Text
          as="div"
          size={large ? '3' : '2'}
          weight={large ? 'bold' : 'regular'}
          truncate
          title={typeof title === 'string' ? title : undefined}
          translate="no"
        >
          {title}
        </Text>
        <Text
          as="div"
          size={large ? '2' : '1'}
          color="gray"
          className={styles.mediaItemCaption}
          translate="no"
        >
          {caption}
        </Text>
      </div>
    </div>
  )
}

export default MediaItem
